import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-admin';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { fmtDate } from '../../shared/Panel';
import { plural } from '../../shared/plural';
import { hasToken, useSessionReady } from '../../hooks/useSessionReady';
import {
  DEPARTMENT_LABELS,
  FEATURE_NOTIFICATION_READ_STATE,
  audienceText,
  fetchNotifications,
  followedForApi,
  importantFirst,
  markNotificationRead,
  markNotificationsRead,
  selectedFromApi,
  setFollowedDepartments,
  type NotificationFeed,
} from './notificationsApi';
import { useFeature } from '../featureFlags/FeatureGate';

/**
 * Powiadomienia działów w pasku górnym (I.04).
 *
 * Przekazanie do Trade, zwrot do FTL i zamknięcie zlecenia (sprzedaż,
 * nieaktualne) — czytane przez API z dziennika zmian. Front nie zna reguły:
 * pokazuje to, co przyszło, i pozwala wybrać działy do śledzenia.
 *
 * Błąd pobrania chowa dzwonek — komunikat przy każdym odświeżeniu byłby
 * hałasem, jak przy liczniku bezczynnych. Bez sesji nie pyta wcale
 * (`useSessionReady`) — na ekranie logowania każde zapytanie dawało 401.
 *
 * Decyzja użytkownika 12.10.2026 („B4 — wyróżnij te ważniejsze alerty”):
 * licznik jest czerwony tylko wtedy, gdy wśród nieprzeczytanych jest ważny
 * alert (zmiana w TMS, anulacja, zmiana pojazdu, pilne przeplanowanie,
 * utrata pojazdu, jutrzejszy załadunek bez auta); inaczej szary. Ważne
 * stoją na górze listy pod nagłówkiem „Ważne”, z czerwoną ikoną, a reszta
 * pod spodem. Którą ważność ma wpis, mówi API. Za przełącznikiem
 * `importantAlerts` — wyłączony: dzwonek jak na produkcji (czerwony licznik
 * zawsze, lista w kolejności z API, bez grup i wyróżnień).
 */

/** Co ile odświeżamy — przekazanie ma dotrzeć do drugiego działu szybko. */
export const REFRESH_MS = 60_000;

/**
 * Autor wpisu, gdy zmiany nie zrobił człowiek (import z TMS, kolejka) —
 * „system”, jak w historii zmian zlecenia, nie potoczne „automat” (poprawa
 * brzmienia 01.10.2026).
 */
export const SYSTEM_AUTHOR = 'system';

/**
 * Znacznik rozwiązanego alertu (24.10.2026, S-24.10-09, przełącznik
 * `notificationReadState`) — niezależny od „przeczytane”.
 */
export const STALE_LABEL = 'nieaktualne';
export const STALE_HINT =
  'Warunek alertu już nie trwa (potwierdzony, rozpatrzony albo zlecenie ma już wykonawcę) — zostaje w historii.';

/** Znacznik „nieaktualne” — wspólny dla dzwonka i strony powiadomień. */
export const StaleChip = ({ testId = 'notification-stale' }: { testId?: string }) => (
  <Tooltip title={STALE_HINT}>
    <Chip size="small" variant="outlined" label={STALE_LABEL} sx={{ ml: 0.5, height: 18 }} data-testid={testId} />
  </Tooltip>
);

/**
 * Nazwa dzwonka dla czytnika ekranu — z odmianą, nie „1 nieprzeczytanych”;
 * ważne alerty dopowiedziane osobno.
 */
export const bellLabel = (unread: number, importantUnread = 0) =>
  `Powiadomienia: ${plural(unread, 'nieprzeczytane', 'nieprzeczytane', 'nieprzeczytanych')}` +
  (importantUnread > 0 ? `, w tym ważne: ${importantUnread}` : '');

/** Licznik bez ważnych alertów — szary zamiast czerwonego. */
const MUTED_BADGE_SX = { '& .MuiBadge-badge': { bgcolor: 'grey.600', color: 'common.white' } } as const;

export const NotificationBell = () => {
  const [feed, setFeed] = useState<NotificationFeed | null>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const ready = useSessionReady();
  const importantOn = useFeature('importantAlerts');
  // Strona „Powiadomienia” z historią (15.10.2026, Etap 1 makiety FTL).
  const historyOn = useFeature('notificationsPage');
  // „Przeczytane” osobno i „nieaktualne” (24.10.2026, S-24.10-09).
  const readStateOn = useFeature(FEATURE_NOTIFICATION_READ_STATE);

  const load = useCallback(async () => {
    // Token mógł zniknąć między odświeżeniami (wylogowanie w innej karcie).
    if (!hasToken()) return;
    try {
      setFeed(await fetchNotifications());
    } catch {
      setFeed(null);
    }
  }, []);

  useEffect(() => {
    if (!ready) {
      setFeed(null);
      return undefined;
    }
    void load();
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(timer);
  }, [load, ready]);

  if (feed === null) return null;

  const selected = selectedFromApi(feed.followed, feed.departments);
  const importantUnread = importantOn ? (feed.importantUnread ?? 0) : 0;
  const items = importantOn ? importantFirst(feed.items) : feed.items;
  const isImportant = (i: (typeof items)[number]) => importantOn && i.important === true;
  const importantCount = items.filter(isImportant).length;
  // Licznik szary tylko z przełącznikiem i bez ważnych — inaczej czerwony, jak dotąd.
  const redBadge = !importantOn || importantUnread > 0;
  // Z „nieaktualne” licznik z API liczy tylko aktualne (decyzja użytkownika
  // 24.10.2026) — nieaktualne nieprzeczytane też można oznaczyć hurtem.
  const anyUnread = feed.unread > 0 || (readStateOn && feed.items.some((i) => i.unread));
  const toggle = async (department: string) => {
    const next = selected.includes(department)
      ? selected.filter((d) => d !== department)
      : [...selected, department];
    await setFollowedDepartments(followedForApi(next, feed.departments));
    await load();
  };
  const readAll = async () => {
    await markNotificationsRead();
    await load();
  };
  // Jedna pozycja — „Oznacz jako przeczytane” albo przejście do zlecenia
  // („otwórz →” z makiety). Błąd zapisu nie zatrzymuje przejścia.
  const readOne = async (key: string | undefined) => {
    if (!readStateOn || !key) return;
    try {
      await markNotificationRead(key);
    } catch {
      return;
    }
    await load();
  };

  return (
    <>
      <Tooltip title="Powiadomienia działów FTL i Trade">
        <IconButton
          color="inherit"
          aria-label={bellLabel(feed.unread, importantUnread)}
          onClick={(e) => setAnchor(e.currentTarget)}
        >
          <Badge
            badgeContent={feed.unread}
            color={redBadge ? 'error' : 'default'}
            max={99}
            sx={redBadge ? undefined : MUTED_BADGE_SX}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {/* 480 px: „Oznacz wszystkie jako przeczytane” mieści się obok tytułu
            w jednej linii (przy 420 px tytuł łamał się na dwie). */}
        <Box sx={{ p: 1.5, width: 480, maxHeight: 520, overflowY: 'auto' }}>
          <Stack direction="row" sx={{ alignItems: 'center', mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              Powiadomienia działów
            </Typography>
            <Button size="small" onClick={() => void readAll()} disabled={!anyUnread}>
              Oznacz wszystkie jako przeczytane
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Obserwowane działy:
            </Typography>
            {feed.departments.map((d) => (
              <FormControlLabel
                key={d}
                control={
                  <Checkbox
                    size="small"
                    checked={selected.includes(d)}
                    // Ostatniego działu nie da się odznaczyć — pusta lista
                    // znaczy „wszystkie”, więc wyglądałoby to odwrotnie.
                    disabled={selected.length === 1 && selected.includes(d)}
                    onChange={() => void toggle(d)}
                  />
                }
                label={<Typography variant="caption">{DEPARTMENT_LABELS[d] ?? d}</Typography>}
              />
            ))}
          </Stack>
          {historyOn && (
            <Typography variant="caption" component="div">
              <Link to="/notifications" onClick={() => setAnchor(null)}>
                Wszystkie powiadomienia — historia i filtry
              </Link>
            </Typography>
          )}
          <Divider sx={{ my: 1 }} />
          {feed.items.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              W ostatnich dwóch tygodniach nie było przekazań między działami ani
              zamknięć zleceń.
            </Typography>
          )}
          {items.map((i, index) => (
            <Box key={i.id}>
              {/* Nagłówki grup tylko wtedy, gdy są ważne — inaczej lista jak dotąd. */}
              {importantCount > 0 && index === 0 ? (
                <Typography variant="overline" sx={{ color: 'error.main', display: 'block' }}>
                  Ważne ({importantCount})
                </Typography>
              ) : null}
              {importantCount > 0 && index === importantCount ? (
                <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                  Pozostałe
                </Typography>
              ) : null}
              <Box
                data-testid={isImportant(i) ? 'notification-important' : 'notification'}
                sx={{
                  py: 0.75,
                  ...(isImportant(i)
                    ? { borderLeft: 3, borderColor: 'error.main', pl: 1 }
                    : {}),
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: i.unread ? 600 : 400 }}>
                  {isImportant(i) ? (
                    <ErrorOutlineRoundedIcon
                      aria-label="Ważne"
                      sx={{ fontSize: 16, color: 'error.main', verticalAlign: 'text-bottom', mr: 0.5 }}
                    />
                  ) : null}
                  <Link
                    to={`/user-offers/${i.order.id}/show`}
                    onClick={() => {
                      setAnchor(null);
                      if (i.unread) void readOne(i.key);
                    }}
                  >
                    {i.order.externalId ?? `#${i.order.id}`}
                  </Link>
                  {' — '}
                  {i.text}
                  {readStateOn && i.stale ? <StaleChip /> : null}
                </Typography>
                <Stack direction="row" sx={{ alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                    {fmtDate(i.at)} · {i.by ?? SYSTEM_AUTHOR} · dla: {audienceText(i)}
                  </Typography>
                  {readStateOn && i.unread && i.key ? (
                    <Button
                      size="small"
                      sx={{ py: 0, minWidth: 0, fontSize: 12 }}
                      onClick={() => void readOne(i.key)}
                      data-testid="notification-mark-read"
                    >
                      Oznacz jako przeczytane
                    </Button>
                  ) : null}
                </Stack>
              </Box>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
};
